import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { DELIVERED_STATUS_ID, type Delivery, type DeliveryBoardStatus, type Supplier } from '@interfaces/ILogistics';
import { supplierById } from '@d-orca/shared/client/logistics';
import { DragHandleDotsIcon } from '../../IssuesPage/Board/icons';
import {
  DELIVERED_WINDOW_DAYS,
  columnCards,
  criticalFirst,
  displayStatusName,
  partitionDelivered,
  sortColumnCards,
  sortDeliveredCards,
  type DragData,
  type IColumnCard,
} from '../board.logic';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { BoardCard } from './BoardCard';
import styles from './DeliveryBoard.module.css';

export interface IBoardColumnProps {
  status: DeliveryBoardStatus;
  cards: Delivery[];
  tz: string;
  now: Date;
  showAll: boolean;
  onToggleShowAll: () => void;
  /** Ref currently being deleted — its card fades under a "Deleting…" overlay.
   *  Board-wide (not per-column) because a delete can be in flight while the
   *  user has scrolled to a different column. */
  deletingRef: string | null;
  /** Ref just dropped on another column, still waiting for the deliveries prop
   *  to reflect the move — its card keeps the drag fade. Board-wide like
   *  `deletingRef`: the card is fading in one column and about to mount in
   *  another. */
  dropPendingRef: string | null;
  /** Jump target raised elsewhere on the board — carries a nonce so a repeat
   *  jump to the same card replays the flash. Board-wide, same reason as
   *  `deletingRef`; BoardColumn derives the per-card `flash` prop from it. */
  flashRef: { ref: string; nonce: number } | null;
  onFlashEnd: () => void;
  registerNode: (ref: string, el: HTMLElement | null) => void;
  onOpenDetail: (d: Delivery) => void;
  /** Stars or unstars an order; absent, the star only reads. */
  onToggleCritical?: (d: Delivery) => void;
  /** The Materials search has a query. The Delivered column's 14-day recency
   *  fold is suspended for the duration: the user asked for these cards BY
   *  NAME, and hiding a hit behind "Show all (1)" under a "no deliveries"
   *  placeholder reads as a contradiction. */
  searchActive?: boolean;
  /** The caller's role may reorder columns: without it no grip is rendered. */
  canEdit: boolean;
  /** The site's suppliers — each card shows its order's supplier name. */
  suppliers: Supplier[];
}

export function BoardColumn({
  status,
  cards,
  tz,
  now,
  showAll,
  onToggleShowAll,
  deletingRef,
  dropPendingRef,
  flashRef,
  onFlashEnd,
  registerNode,
  onOpenDetail,
  onToggleCritical,
  searchActive = false,
  canEdit,
  suppliers,
}: IBoardColumnProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: status.id,
    data: { type: 'column', statusId: status.id } satisfies DragData,
    // Delivered is pinned rightmost: it never starts a drag, but stays a drop
    // target so cards can still land on it.
    disabled: { draggable: status.system, droppable: false },
  });
  const isDelivered = status.id === DELIVERED_STATUS_ID;
  // One card per truck. The per-material view of arrivals lives in Delivery
  // History now, with the synced orders it lists.
  const faces = columnCards(cards);
  const sorted = isDelivered ? criticalFirst(sortDeliveredCards(faces)) : sortColumnCards(faces, now, tz);
  const { recent, older } =
    isDelivered && !searchActive
      ? partitionDelivered(sorted, now, tz)
      : { recent: sorted, older: [] };
  const renderCard = ({ face: delivery }: IColumnCard) => (
    <BoardCard
      key={delivery.ref}
      delivery={delivery}
      tz={tz}
      now={now}
      supplierName={supplierById(suppliers, delivery.supplierId)?.name ?? null}
      deleting={delivery.ref === deletingRef}
      dropPending={delivery.ref === dropPendingRef}
      flash={flashRef?.ref === delivery.ref ? { nonce: flashRef.nonce } : null}
      onFlashEnd={onFlashEnd}
      onOpenDetail={onOpenDetail}
      onToggleCritical={onToggleCritical}
      registerNode={registerNode}
    />
  );

  return (
    <div
      ref={setNodeRef}
      // Names the column root for tests: `.column` and `.columnHead` both match
      // a `[class*="column"]` selector, and the head is the nearer ancestor of
      // the name. Same idiom as StatusesMenu's `data-row`.
      data-column={status.id}
      className={styles.column}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : undefined }}
    >
      {canEdit && !status.system ? (
        <div className={styles.columnGrip} {...attributes} {...listeners} aria-label={t('logistics.board.reorderHandle')}>
          <DragHandleDotsIcon />
        </div>
      ) : null}
      <div className={styles.columnHead}>
        {/* Delivered lists only the arrivals still waiting on their sync — a
            synced one has moved to Delivery History — so it says so. */}
        <span className={styles.columnName}>
          {isDelivered ? t('logistics.board.deliveredNotSynced') : displayStatusName(status, t)}
        </span>
        {/* What the column lists, windowed or not: the Show all link names the
            hidden part. */}
        <span className={styles.count}>{sorted.length}</span>
      </div>
      <ScrollArea className={styles.stackFrame} viewportClassName={styles.stack}>
        {recent.length === 0 ? (
          <div className={styles.columnEmpty}>
            {/* Only old arrivals: the STATUS is not empty, the window is. */}
            {older.length > 0
              ? t('logistics.board.emptyRecent', { n: DELIVERED_WINDOW_DAYS })
              : t('logistics.board.emptyColumn')}
          </div>
        ) : (
          recent.map(renderCard)
        )}
        {/* The toggle marks the window's edge: recent cards above it, and —
            expanded — the older ones below it. */}
        {older.length > 0 ? (
          <button type="button" className={styles.showAll} onClick={onToggleShowAll}>
            {showAll ? t('logistics.board.showRecent') : t('logistics.board.showAll', { n: older.length })}
          </button>
        ) : null}
        {showAll ? older.map(renderCard) : null}
      </ScrollArea>
    </div>
  );
}
